import type { Identifiers } from '@atlaskit/feature-gate-js-client';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../stores/redux.store';
import { getFfsId } from '../utilities/ffs-id';
import { FeatureGatesInit } from './FeatureGatesInit';
import { useAnalyticsAnonymousId } from '../utilities/analytics/analytics-web-client';
import { ConfigCollection } from '@atlaskit/web-config-client';
import Spinner from '@atlaskit/spinner';
import { useIntl } from 'react-intl';
import {
  DynamicConfigsContext,
  initializeDynamicConfigs,
} from '../utilities/feature-flags/dynamic-config';
import { captureException } from '../utilities/analytics/error-reporting';
import pDefer from 'p-defer';

type FeatureGateProviderProps = {
  children: ReactNode;
};

/**
 * Feature Gates are currently created within React (ticket to move this outside in KIRBY-9193)
 * There are cases where FGs may be needed to used by entities outside of React components
 * The example this was written for is the react-router v6 loader function,
 * which gets called at the top-level on initialization of createBrowserRouter is called.
 */
const deferredPromise = pDefer<void>();

// Auto-resolve in test environment to prevent hanging tests, except when testing blocking behavior
// Tests can set environment variable TEST_FEATURE_GATE_BLOCKING=true to test actual blocking
if (process.env.NODE_ENV === 'test' && process.env.TEST_FEATURE_GATE_BLOCKING !== 'true') {
  deferredPromise.resolve();
}
export const isFeatureGateProviderReady = deferredPromise.promise;

export const FeatureGateProvider = ({ children }: FeatureGateProviderProps) => {
  const [isFeatureGatesClientReady, setIsFeatureGatesClientReady] = useState(false);
  const [dynamicConfigsClient, setDynamicConfigsCollection] = useState<
    ConfigCollection | null | undefined
  >(undefined);

  const tenantId = useAppSelector(state => state.context?.tenantCloudId);
  const atlassianAccountId = useAppSelector(state => state.context?.userId);
  const ffsId = getFfsId();
  const { formatMessage } = useIntl();
  const analyticsAnonymousId = useAnalyticsAnonymousId();

  const featureGatesIdentifiers: Identifiers = useMemo(
    () => ({
      tenantId,
      atlassianAccountId,
      randomizationId: ffsId,
      analyticsAnonymousId,
    }),
    [tenantId, atlassianAccountId, ffsId, analyticsAnonymousId]
  );

  useEffect(() => {
    initializeDynamicConfigs(analyticsAnonymousId).then(dynamicConfigs =>
      setDynamicConfigsCollection(dynamicConfigs)
    );
  }, [analyticsAnonymousId]);

  const onFeatureGatesReady = useCallback((err?: Error | undefined) => {
    setIsFeatureGatesClientReady(true);
    captureStatsigFeatureGateError(err);
    deferredPromise.resolve();
  }, []);

  const areClientsReady = isFeatureGatesClientReady && !!dynamicConfigsClient;

  return (
    <>
      <FeatureGatesInit identifiers={featureGatesIdentifiers} onReady={onFeatureGatesReady} />

      {!areClientsReady && (
        <Spinner
          label={formatMessage({
            id: 'statsig.clients.loading',
            defaultMessage: 'Loading feature flags...',
            description:
              'Label text for loading spinner when Statsig clients are being initialized',
          })}
        />
      )}

      {areClientsReady && (
        <DynamicConfigsContext.Provider value={dynamicConfigsClient}>
          {children}
        </DynamicConfigsContext.Provider>
      )}
    </>
  );
};

const captureStatsigFeatureGateError = (err?: Error | undefined) => {
  if (!err) return;

  captureException(err, undefined, { statsigFeatureGate: 'statsig_feature_gate_client_init' });
};
